import { humanizeEnum } from '@helpers/labels';

/**
 * HR Ambassadors: shapes and the words the two pages put on screen, kept apart from the pages so
 * they can be tested without rendering them. Field names follow the backend's ambassador.graphqls
 * and referrals.graphqls exactly.
 */

export interface Ambassador {
  _id: string;
  /** EMAIL or GOOGLE. */
  authProvider: string | null;
  personalDetails: {
    firstName: string | null;
    lastName: string | null;
    email: string | null;
    phone: string | null;
    city: string | null;
  } | null;
  firm: string | null;
  /** ACTIVE or SUSPENDED. */
  status: string | null;
  statusReason: string | null;
  code: string | null;
  /** Null until they rename their code, which they may do once. */
  codeRenamedAt: string | null;
  createdAt: string | null;
  lastLoginAt: string | null;
}

export interface TierProgress {
  threshold: number;
  amount: number;
  reached: boolean;
  owed: number | null;
  status: string | null;
}

export interface Funnel {
  invited: number | null;
  registered: number | null;
  profileCompleted: number | null;
  applied: number | null;
  interviewing: number | null;
  hired: number | null;
  qualified: number | null;
}

export interface AmbassadorSummary {
  code: string | null;
  currency: string | null;
  /** Weighted credits toward milestones: a priority-role candidate counts twice. */
  counted: number | null;
  qualified: number | null;
  earned: number | null;
  paid: number | null;
  pendingAmount: number | null;
  /** How many rewards are waiting for review. */
  underReview: number | null;
  badge: string | null;
  nextBadge: { badge: string | null; at: number | null } | null;
  tiers: TierProgress[] | null;
  funnel: Funnel | null;
  payoutUpiId: string | null;
  payoutPhone: string | null;
}

export interface ReferredRow {
  name: string | null;
  role: string | null;
  city: string | null;
  /** SIGNED_UP, VERIFIED, INTERVIEWING or PLACED. */
  stage: string | null;
  status: string | null;
  statusReason: string | null;
  /** Counts twice toward milestones. Null until they qualify. */
  priority: boolean | null;
  joinedAt: string | null;
}

export interface ReferredPage {
  total: number;
  all: number;
  needs: number;
  verified: number;
  interviewing: number;
  placed: number;
  items: ReferredRow[];
}

export interface RewardRow {
  id: string;
  /** PER_PERSON, TIER, PLACEMENT or DRAW. */
  kind: string | null;
  title: string | null;
  amount: number | null;
  currency: string | null;
  status: string | null;
  earnedAt: string | null;
  paidAt: string | null;
  paymentRef: string | null;
  rejectedReason: string | null;
  priority: boolean | null;
}

export interface Invite {
  _id: string;
  name: string | null;
  email: string | null;
  hasResume: boolean | null;
  resumeFileName: string | null;
  /** FIT, UNCLEAR or NOT_FIT; null without a CV. */
  relevance: string | null;
  /** SENT, JOINED or CANCELLED. */
  status: string | null;
  sentAt: string | null;
  lastSentAt: string | null;
  resendCount: number | null;
  joinedAt: string | null;
}

export interface Certificate {
  _id: string;
  /** AMBASSADOR, CERTIFIED or GOLD. */
  level: string | null;
  name: string | null;
  issuedAt: string | null;
  /** False once the ambassador has been suspended. */
  active: boolean | null;
}

export const isSuspended = (a: Pick<Ambassador, 'status'> | null | undefined) =>
  (a?.status ?? '').toUpperCase() === 'SUSPENDED';

/** "Priya Shah", falling back to the email, then the code, so a row is never blank. */
export const ambassadorName = (a: Pick<Ambassador, 'personalDetails' | 'code' | '_id'>) => {
  const pd = a.personalDetails;
  const full = [pd?.firstName, pd?.lastName].map((s) => (s ?? '').trim()).filter(Boolean).join(' ');
  return full || pd?.email || a.code || a._id;
};

export const initialsOf = (name: string) => {
  const words = name.trim().split(/[\s@._-]+/).filter(Boolean);
  return (words.length > 1 ? `${words[0][0]}${words[1][0]}` : (words[0] ?? '').slice(0, 2)).toUpperCase() || 'A';
};

/** The recognition levels, reached at 25, 50 and 100 weighted credits. */
export const BADGE_LABELS: Record<string, string> = {
  AMBASSADOR: 'Ambassador',
  CERTIFIED: 'Certified Ambassador',
  GOLD: 'Gold Ambassador',
};

export const badgeLabel = (b: string | null | undefined) => (b ? BADGE_LABELS[b] ?? humanizeEnum(b) : 'No badge yet');

export const STATUS_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'ACTIVE', label: 'Active' },
  { value: 'SUSPENDED', label: 'Suspended' },
];

/**
 * The funnel in the order a candidate moves through it. `hired` is labelled Placed because that is
 * the word the ambassador's own dashboard and the Placed tab use for it.
 */
export const FUNNEL_STEPS: { key: keyof Funnel; label: string }[] = [
  { key: 'invited', label: 'Invited' },
  { key: 'registered', label: 'Registered' },
  { key: 'profileCompleted', label: 'Profile completed' },
  { key: 'applied', label: 'Applied' },
  { key: 'interviewing', label: 'Interviewed' },
  { key: 'hired', label: 'Placed' },
];

export type PeopleTab = 'all' | 'needs' | 'verified' | 'interviewing' | 'placed';

export const PEOPLE_TABS: { id: PeopleTab; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'needs', label: 'Needs action' },
  { id: 'verified', label: 'Verified' },
  { id: 'interviewing', label: 'Interviewing' },
  { id: 'placed', label: 'Placed' },
];

/** CRMStatusChip keys, so stages share the console's stage colours. */
export const STAGE_TONE: Record<string, string> = {
  SIGNED_UP: 'new',
  VERIFIED: 'approved',
  INTERVIEWING: 'interviewing',
  PLACED: 'hired',
};

export const INVITE_STATUS_TONE: Record<string, string> = {
  SENT: 'pending',
  JOINED: 'success',
  CANCELLED: 'inactive',
};

export const REWARD_STATUS_TONE: Record<string, string> = {
  PENDING_REVIEW: 'needsreview',
  EARNED: 'approved',
  PAID: 'success',
  REJECTED: 'failed',
  COVERED: 'inactive',
};

/** "Milestone: 10 credits" when the backend sends no title for a row. */
export const rewardTitleOf = (r: Pick<RewardRow, 'title' | 'kind'>) => r.title || humanizeEnum(r.kind ?? 'PER_PERSON');

/** "UPI priya@okaxis, Phone 9876543210", or null when they have given neither. */
export const payoutText = (s: Pick<AmbassadorSummary, 'payoutUpiId' | 'payoutPhone'> | null | undefined) => {
  const parts = [s?.payoutUpiId ? `UPI ${s.payoutUpiId}` : null, s?.payoutPhone ? `Phone ${s.payoutPhone}` : null].filter(Boolean);
  return parts.length ? parts.join(', ') : null;
};

/** How far to the next badge, e.g. "12 credits to Certified Ambassador". Null at Gold. */
export const nextBadgeText = (s: Pick<AmbassadorSummary, 'counted' | 'nextBadge'> | null | undefined) => {
  const next = s?.nextBadge;
  if (!next?.badge || typeof next.at !== 'number') return null;
  const left = Math.max(0, next.at - (s?.counted ?? 0));
  return `${left} ${left === 1 ? 'credit' : 'credits'} to ${badgeLabel(next.badge)}`;
};
